import React from 'react';
import { DatabaseBackup, Play, Save } from 'lucide-react';
import InputField from '../../Forms/Input/InputField';
import SelectField from '../../Forms/Input/SelectField';
import ToggleSwitch from '../../Forms/Input/ToggleSwitch';
import { useBackup } from '../../../hooks/settings/useBackup';
import { BackupFrequency } from '../../../types/settings/backup';
import TimePicker from '../../Forms/Time/TimePicker';

const frequencyOptions = [
  { label: 'Diario', value: 'DAILY' },
  { label: 'Semanal', value: 'WEEKLY' },
  { label: 'Mensual', value: 'MONTHLY' },
];

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Sin registro';

  return new Date(value).toLocaleString('es-SV', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const BackupConfigurationForm: React.FC = () => {
  const {
    form,
    updateField,
    handlerSaveConfiguration,
    handlerRunBackup,
    isSubmitting,
    isRunning,
  } = useBackup();

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 border-b border-stroke pb-6 dark:border-strokedark lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
            <DatabaseBackup size={22} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-black dark:text-white">
              Respaldo del Sistema
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mt-1 max-w-2xl text-sm">
              Configura la programación de copias de seguridad y ejecuta un
              respaldo manual cuando sea necesario.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handlerRunBackup}
          disabled={isRunning || isSubmitting}
          className="inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-opacity-90 disabled:opacity-70"
        >
          <Play size={18} />
          {isRunning ? 'Ejecutando...' : 'Ejecutar respaldo'}
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded border border-stroke bg-white p-4 dark:border-strokedark dark:bg-boxdark">
          <p className="text-gray-500 dark:text-gray-400 text-xs font-semibold uppercase">
            Estado
          </p>
          <p className="mt-2 text-base font-semibold text-black dark:text-white">
            {form.enabled ? 'Programado' : 'Desactivado'}
          </p>
        </div>
        <div className="rounded border border-stroke bg-white p-4 dark:border-strokedark dark:bg-boxdark">
          <p className="text-gray-500 dark:text-gray-400 text-xs font-semibold uppercase">
            Frecuencia
          </p>
          <p className="mt-2 text-base font-semibold text-black dark:text-white">
            {frequencyOptions.find((item) => item.value === form.frequency)
              ?.label || form.frequency}
          </p>
        </div>
        <div className="rounded border border-stroke bg-white p-4 dark:border-strokedark dark:bg-boxdark">
          <p className="text-gray-500 dark:text-gray-400 text-xs font-semibold uppercase">
            Última ejecución
          </p>
          <p className="mt-2 text-sm font-medium text-black dark:text-white">
            {formatDateTime(form.lastExecution)}
          </p>
        </div>
        <div className="rounded border border-stroke bg-white p-4 dark:border-strokedark dark:bg-boxdark">
          <p className="text-gray-500 dark:text-gray-400 text-xs font-semibold uppercase">
            Próxima ejecución
          </p>
          <p className="mt-2 text-sm font-medium text-black dark:text-white">
            {formatDateTime(form.nextExecution)}
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-4">
            <ToggleSwitch
              label="Respaldos automáticos"
              name="enabled"
              checked={form.enabled}
              onChange={() => updateField('enabled', !form.enabled)}
              activeColor="success"
              inactiveColor="danger"
            />

            <ToggleSwitch
              label="Comprimir archivo"
              name="compressEnabled"
              checked={form.compressEnabled}
              onChange={() =>
                updateField('compressEnabled', !form.compressEnabled)
              }
              activeColor="success"
              inactiveColor="danger"
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <SelectField
              label="Frecuencia"
              name="frequency"
              value={form.frequency}
              onChange={(event) =>
                updateField('frequency', event.target.value as BackupFrequency)
              }
              options={frequencyOptions}
              iconKey="calendar"
              required
            />

            <TimePicker
              label="Hora de ejecución"
              name="executionTime"
              value={form.executionTime || ''}
              onChange={(value) => updateField('executionTime', value)}
              iconKey="clock"
              required
            />
          </div>
        </div>

        <InputField
          label="Directorio de respaldo"
          name="backupDirectory"
          type="text"
          value={form.backupDirectory}
          onChange={(event) =>
            updateField('backupDirectory', event.target.value)
          }
          placeholder="C:\\CodenovaPOS\\backups"
          iconKey="archive"
          required
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <InputField
            label="Días de retención"
            name="retentionDays"
            type="number"
            value={form.retentionDays}
            min={1}
            onChange={(event) =>
              updateField('retentionDays', Number(event.target.value))
            }
            iconKey="clock"
            required
          />

          <InputField
            label="Cantidad máxima de archivos"
            name="maxFileCount"
            type="number"
            value={form.maxFileCount}
            min={1}
            onChange={(event) =>
              updateField('maxFileCount', Number(event.target.value))
            }
            iconKey="archive"
            required
          />
        </div>
      </div>

      <div className="flex justify-end border-t border-stroke pt-4 dark:border-strokedark">
        <button
          type="button"
          onClick={handlerSaveConfiguration}
          disabled={isSubmitting || isRunning}
          className="inline-flex items-center justify-center gap-2 rounded bg-primary px-6 py-2.5 font-medium text-white hover:bg-opacity-90 disabled:opacity-70"
        >
          <Save size={18} />
          {isSubmitting ? 'Guardando...' : 'Guardar configuración'}
        </button>
      </div>
    </div>
  );
};

export default BackupConfigurationForm;
